import {
    ApplicationConfig,
    ErrorHandler,
    importProvidersFrom,
    inject,
    LOCALE_ID,
    provideAppInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { GlobalErrorHandler } from '../../../../libs/config/services/global-error-handler';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { HttpClient, provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { IonicRouteStrategy, ModalController, PopoverController, provideIonicAngular } from '@ionic/angular';
import { IonicStorageModule } from '@ionic/storage-angular';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { AuthInterceptor } from '../../../../libs/common/services/interceptor/auth.interceptor';
import { OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc';
import { environment } from '../environments/environment';
import { SplashScreenStateService } from '../../../../libs/common/services/splash-screen-state.service';
import { provideTranslateService } from '@ngx-translate/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideMarkdown } from 'ngx-markdown';
import { TOAST_SERVICE_INTERFACE } from '../../../../libs/common/services/toast-service/toast-service.interface';
import { ToastService } from '../../../../libs/common/services/toast-service/toast-service.service';
import { LoggingService } from '../../../../libs/logging/logging.service';
import { routes } from './app.routes';
import { oAuthStorageFactory } from '../../../../libs/authentication/oauth-storage.factory';
import { TraceInterceptor } from '../../../../libs/common/services/interceptor/trace.interceptor';
import { CurafidaAuthService } from '../../../../libs/authentication/curafida-auth.service';
import { ENVIRONMENT_CONFIG } from '../../../../libs/config/environment-config.token';
import { FrontendConfigService } from '../../../../libs/config/services/frontend-config.service';
import { ApiService } from '../../../../libs/api/api.service';
import { ConfigService } from '../../../../libs/config/services/config.service';

export const appConfig: ApplicationConfig = {
    providers: [
        /**
         * Angular 21 no longer wires Zone.js into change detection by default. Ionic's overlay
         * results and lifecycle hooks (ionViewWillEnter and friends) mutate component state from
         * async callbacks, which a zoneless app would not re-render without signals or an explicit
         * markForCheck(). Keep Zone-based change detection until that is migrated deliberately.
         * `zone.js` itself is still loaded from src/polyfills.ts.
         */
        provideZoneChangeDetection(),
        { provide: OAuthStorage, useFactory: oAuthStorageFactory },
        { provide: ENVIRONMENT_CONFIG, useFactory: () => environment },
        /**
         * This initializer runs before Angular renders anything. Every line
         * added here directly increases the time users stare at a blank screen.
         *
         * The four steps below are strictly sequential because each depends on
         * the result of the previous one.
         *
         * If you need to initialize something else, ask yourself:
         *   - Can it be lazy-loaded on first use instead?
         *   - Can it run in parallel with one of the steps?
         *   - Does it truly need to block rendering for every user on every load?
         */
        provideAppInitializer(() => {
            const frontendConfigService = inject(FrontendConfigService);
            const authService = inject(CurafidaAuthService);
            const apiService = inject(ApiService);
            const configService = inject(ConfigService);
            return (async () => {
                /* 1. Fetch and cache the frontend config (KeyCloak config, feature flags, etc.) */
                frontendConfigService.init();
                /* 2. Discover and configure KeyCloak; loads tokens from storage if present */
                await authService.init();
                /* 3. Resolve the backend API base URL from the token claims; fetches the API root */
                await apiService.init();
                /* 4. Fetch backend configuration using the resolved API URL.
                 * Fire-and-forget: config assets (logo, background, favicon) are cosmetic and
                 * must not block rendering. Signals update reactively once the config resolves. */
                configService.init();
            })();
        }),
        provideIonicAngular({
            platform: {
                mobile: (win) => {
                    return (
                        /capacitor|cordova|iphone|mobileweb|mobile/i.test(win.navigator.userAgent) &&
                        !/tablet|phablet|ipad/i.test(win.navigator.userAgent)
                    );
                },
            },
            useSetInputAPI: true, //allow to use signal input in modals
        }),
        importProvidersFrom(IonicStorageModule.forRoot()),
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        provideRouter(routes),
        provideCharts(withDefaultRegisterables()),
        provideHttpClient(withXhr(), withInterceptors([AuthInterceptor, TraceInterceptor])),
        provideOAuthClient(),
        { provide: LOCALE_ID, useValue: 'de' },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        SplashScreenStateService,
        provideTranslateService({
            defaultLanguage: 'de',
        }),
        provideAnimations(), // required animations providers
        provideMarkdown({
            loader: HttpClient,
        }),
        { provide: TOAST_SERVICE_INTERFACE, useExisting: ToastService },
        { provide: 'LOGGER_FACTORY', useClass: LoggingService },
        ModalController,
        PopoverController,
    ],
};
